import { html, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import EkoElement from "@/utilities/element";
import style from "./toast.css?inline";

@customElement("eko-toast")
class Toast extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private dismissAt: number | null = null;

  private timeLeft: number | null = null;

  private timerId: number | null = null;

  @property({ reflect: true, attribute: "auto-dismiss" }) autoDismiss: string = "5000";

  @property({ reflect: true, type: Boolean, attribute: "disable-animation" })
  disableAnimation: boolean = false; // required across page navigation when the toast has already been animated in

  @property({ type: Boolean }) isExiting = false;

  @property({ reflect: true })
  theme: "default" | "danger" = "default";

  // Dismiss event
  private dismiss() {
    if (this.isExiting) return;

    this.isExiting = true;
    this.setAttribute("exiting", "");

    if (this.timerId !== null) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }

    this.timeLeft = null;
    this.dismissAt = null;

    // Wait for exiting CSS animation to complete
    this.addEventListener(
      "animationend",
      () => {
        this.dispatchEvent(new CustomEvent("dismiss", { bubbles: true }));
      },
      { once: true }
    );
  }

  private pauseTimer() {
    if (this.isExiting || this.timerId === null) return;

    window.clearTimeout(this.timerId);
    this.timerId = null;

    if (this.dismissAt === null) return;

    this.timeLeft = this.dismissAt - Date.now();
  }

  private resumeTimer() {
    if (this.isExiting || this.timeLeft === null) return;

    this.dismissAt = Date.now() + this.timeLeft;
    this.timerId = window.setTimeout(() => this.dismiss(), this.timeLeft);
    this.timeLeft = null;
  }

  // Event listener for autoDismiss
  connectedCallback() {
    super.connectedCallback();

    // Read from attribute if it exists, otherwise use property default
    const dismissAttr = this.getAttribute("auto-dismiss");
    const delay = dismissAttr ? parseInt(dismissAttr, 10) : parseInt(this.autoDismiss, 10);

    if (delay > 0) {
      this.dismissAt = Date.now() + delay;
      this.timerId = window.setTimeout(() => this.dismiss(), delay);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.timerId !== null) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }

    this.dismissAt = null;
    this.timeLeft = null;
  }

  render() {
    return html`
      <div
        class="eko-toast"
        part="root"
        @mouseenter=${this.pauseTimer}
        @mouseleave=${this.resumeTimer}>
        <span class="eko-toast__content" part="content">
          <slot></slot>
        </span>
        <eko-button variant="plain" square part="close" @click=${this.dismiss}
          ><eko-icon name="x-close"></eko-icon
        ></eko-button>
      </div>
    `;
  }
}

export type ToastElement = Toast & HTMLElement;

declare global {
  interface HTMLElementTagNameMap {
    "eko-toast": ToastElement;
  }
}

export default Toast;
